import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LsqListSource, Pagination } from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ADMIN_MANAGE_PROSPECT, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    OperationService,
    Prospect,
    ProspectData,
    ProspectLight,
    ProspectService,
    SchemaService
} from '@lsq-frontend/remote-services/ar-app';
import { ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import { DrApplicationService } from '@lsqlabs/dr-application-shared';
import {
    Color,
    FeatureFlagService,
    forLifeOf,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    LifecycleAware,
    Modal
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { orderBy } from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, startWith } from 'rxjs/operators';
import { AdminProspectsService } from './admin-prospects.service';
import { InviteProspectModalComponent } from './modals/invite-prospect-modal.component';
import { InitializeProspectModalComponent } from './modals/initialize-prospect-modal.component';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

@LifecycleAware()
@Component({
    selector: 'prospects-admin',
    templateUrl: './admin-prospects.component.html',
    styleUrls: ['./admin-prospects.component.scss']
})
export class AdminProspectsComponent implements OnInit, OnDestroy {
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly prospectCreationSuccessMessage = 'Prospect created successfully.';
    public readonly emailSentSuccessMessage = 'Invitation sent successfully.';
    public filterOptionsStatus: string[];
    public filterOptionsProspectTypes: string[];
    public filterOptionsInvitedBy: string[];
    public filterOptionsInvitedByDisable: boolean = true;
    public allProspectTypes = 'All Types';
    public allProspectStatus = 'All Status';
    public allInvitedBy = 'All Invited By';

    public filtersFormGroup = new FormGroup({
        prospectStatusFilter: new FormControl(this.allProspectStatus),
        prospectTypesFilter: new FormControl(this.allProspectTypes),
        invitedByFilter: new FormControl(this.allInvitedBy),
        prospectName: new FormControl()
    });
    public adminManageProspectsLinkUrl = getRouterLinkUrlById(atlas, ADMIN_MANAGE_PROSPECT);
    public getProspectRequest$ = new BehaviorSubject<
        Parameters<ProspectService['getProspectDataLight']>
    >([undefined, undefined, undefined, undefined]);
    private _loading = new BehaviorSubject<boolean>(false);
    public loading = this._loading.asObservable();
    private _sort$ = new BehaviorSubject<Partial<Pagination>>({});
    public userEmail: string;
    private prospectTypes = [
        Prospect.SchemaTypeEnum.ArProspect,
        Prospect.SchemaTypeEnum.ScfBuyerProspect
    ];

    public tableDataSource = new LsqListSource({
        rows$: this._adminProspectsService.prospects,
        loading$: this._adminProspectsService.loading,
        handleSort: (sorting) => {
            this.tableDataSource.sortColumn = sorting.sortColumn;
            this.tableDataSource.sortDirection = sorting.sortDirection;
            this._adminProspectsService.setSort(sorting.sortColumn, sorting.sortDirection);
            this._adminProspectsService.handleSortChange();
            this.setTableSkipTake(0, this._adminProspectsService.take.value);
        },
        totalCount$: this._adminProspectsService.totalCount,
        handleTakeChange: (take: number) => {
            this._adminProspectsService.handleTakeChange(take);
            this.setTableSkipTake(
                this._adminProspectsService.skip.value,
                this._adminProspectsService.take.value
            );
        },
        take: this._adminProspectsService.take.value,
        handleSkipChange: (skip: number) => {
            this._adminProspectsService.handleSkipChange(skip);
            this.setTableSkipTake(
                this._adminProspectsService.skip.value,
                this._adminProspectsService.take.value
            );
        },
        skip: this._adminProspectsService.skip.value
    });

    // TODO: Bring back when we use server side pagination.
    // public tableDataSource = new LsqListSource({
    //     rows$: combineLatest(this._sort$, this.getProspectRequest$.pipe(debounceTime(1000))).pipe(
    //         switchMap(([_, args]) => this.loadRows(args)),
    //         map((prospects) => this.sortProspects(prospects))
    //     ) as Observable<any>,
    //     loading$: this.loading.pipe(map((initialized: boolean) => initialized)),
    //     handleSort: (sorting) => {
    //         //TODO: local sorting for now, we remove this when we implement server pagination
    //         if (sorting) {
    //             this.tableDataSource.sortColumn = sorting.sortColumn;
    //             this.tableDataSource.sortDirection = sorting.sortDirection;
    //             return this.handleProspectListRequestChange(sorting);
    //         }
    //     }
    // });

    constructor(
        private _modalService: ModalService,
        private _prospectService: ProspectService,
        private _operationService: OperationService,
        private _userService: UserService,
        private _arService: DrApplicationService,
        private _alertService: AlertService,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _adminProspectsService: AdminProspectsService,
        private _schemaService: SchemaService,
        private _featureFlagService: FeatureFlagService
    ) {
        // TODO: Bring back when we use server side pagination.
        // this.loadProspects();
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            'v1/prospect/invitation',
            ResponseCode.Conflict
        );
    }

    public async ngOnInit(): Promise<void> {
        this.filterOptionsStatus = [this.allProspectStatus].concat(
            Object.values(ProspectData.StatusEnum) as string[]
        );
        this.filterOptionsProspectTypes = this.getProspectTypeFilterOptions();

        this._userService.user$.pipe(forLifeOf(this)).subscribe(async (user) => {
            this.userEmail = user?.emailAddress;
        });
        await this._adminProspectsService.loadProspects(true, this.prospectTypes);
        this.getInvitedByFilterOptions(this._adminProspectsService.prospectsRaw);
    }

    public ngOnDestroy(): void {}

    public getProspectTypeFilterOptions(): string[] {
        const documentName = this._arService.documentCenterName;
        let prospectType = [];
        for (const schemaType of Object.values(Prospect.SchemaTypeEnum)) {
            if (schemaType !== documentName) {
                let str = this.changeProspectType(schemaType);
                prospectType.push({
                    label: str,
                    value: schemaType
                });
            }
        }
        return [this.allProspectTypes].concat(prospectType);
    }

    // Change prospect type ArProspect to AR Prospect
    public changeProspectType(value): string {
        if (value) {
            const str = value.replace(/([A-Z])/g, ' $1').trim();
            const arr = str.split(' ');
            return (arr[0].toUpperCase() + ' ' + arr.slice(1)).replace(',', ' ');
        }
        return value;
    }

    public getInvitedByFilterOptions(prospects: ProspectLight[]): void {
        this.filterOptionsInvitedBy = [this.allInvitedBy].concat([
            ...new Set(prospects.map((prospect) => prospect.coordinatorUserId).sort())
        ]);
        this.filterOptionsInvitedByDisable = false;
    }

    public loadProspects(
        id: string = undefined,
        businessName: string = undefined,
        status: string = undefined,
        includeDeleted: boolean = undefined
    ): void {
        this.getProspectRequest$.next([
            id,
            businessName,
            status,
            this.prospectTypes,
            includeDeleted
        ]);
    }

    public async handleInviteProspectClick(): Promise<void> {
        const getProspectSchemaResponse = await this._schemaService.getProspectSchema(
            null,
            null,
            true
        );
        const formGroup: FormGroup = new FormGroup({
            contactFirstName: new FormControl('', [
                Validators.required,
                LsqValidators.maxLength(50)
            ]),
            contactLastName: new FormControl('', [
                Validators.required,
                LsqValidators.maxLength(50)
            ]),
            businessName: new FormControl('', [Validators.required, LsqValidators.maxLength(100)]),
            contactEmail: new FormControl('', [
                Validators.required,
                LsqValidators.email,
                LsqValidators.maxLength(50)
            ]),
            prospectSchemaId: new FormControl('', [Validators.required]),
            contactTypeOptions: new FormControl([]),
            coordinatorUserId: new FormControl(this.userEmail, [])
        });
        const modalData = {
            formGroup: formGroup,
            schemaResponse: getProspectSchemaResponse
        };

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const okButtonIsDisabled$ = formGroup.valueChanges.pipe(
            map((_) => !formGroup.valid),
            startWith(!formGroup.valid)
        );

        this._modalService.showModal(
            new Modal({
                title: 'Invite Prospect',
                bodyComponent: InviteProspectModalComponent,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Invite Prospect',
                okButtonIsDisabled$,
                okButtonSize: 'block',
                showCancelButton: true,
                clickOutsideToClose: false,
                data: modalData,
                isSubmitting$: isSubmittingSubject,
                onCancelClick: () => this._modalService.closeModal(),
                onOkClick: async () => {
                    if (formGroup.valid) {
                        isSubmittingSubject.next(true);
                        await this.prospectInvitation(formGroup);
                        isSubmittingSubject.next(false);
                    }
                    await this._adminProspectsService.loadProspects(true, this.prospectTypes);
                }
            })
        );
    }

    public async handleInitializeProspectClick(): Promise<void> {
        const getProspectSchemaResponse = await this._schemaService.getProspectSchema(
            null,
            null,
            true
        );
        const formGroup: FormGroup = new FormGroup({
            businessName: new FormControl('', [Validators.required, LsqValidators.maxLength(100)]),
            prospectSchemaId: new FormControl('', [Validators.required]),
            contactTypeOptions: new FormControl([]),
            coordinatorUserId: new FormControl(this.userEmail, []),
            inviteUser: new FormControl(false),
            contactFirstName: new FormControl('', [LsqValidators.maxLength(50)]),
            contactLastName: new FormControl('', [LsqValidators.maxLength(50)]),
            contactEmail: new FormControl('', [LsqValidators.email, LsqValidators.maxLength(50)]),
            phoneNumber: new FormControl('')
        });
        const modalData = {
            formGroup: formGroup,
            schemaResponse: getProspectSchemaResponse
        };

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const okButtonIsDisabled$ = formGroup.valueChanges.pipe(
            map(() => {
                const businessNameValue = formGroup.get('businessName')?.value;
                const prospectSchemaIdValue = formGroup.get('prospectSchemaId')?.value;
                const inviteUserValue = formGroup.get('inviteUser')?.value;

                if (inviteUserValue) {
                    // All fields are required
                    return !formGroup.valid;
                } else {
                    // Only check if businessName and prospectSchemaId have values
                    return !(businessNameValue && prospectSchemaIdValue);
                }
            }),
            startWith(!formGroup.valid)
        );

        this._modalService.showModal(
            new Modal({
                title: 'Create Prospect',
                bodyComponent: InitializeProspectModalComponent,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Create Prospect',
                okButtonIsDisabled$,
                okButtonSize: 'block',
                showCancelButton: true,
                clickOutsideToClose: false,
                data: modalData,
                isSubmitting$: isSubmittingSubject,
                onCancelClick: () => this._modalService.closeModal(),
                onOkClick: async () => {
                    if (formGroup.valid) {
                        isSubmittingSubject.next(true);
                        await this.initializeProspect(formGroup);
                        isSubmittingSubject.next(false);
                    }
                    await this._adminProspectsService.loadProspects(true, this.prospectTypes);
                }
            })
        );
    }

    public async initializeProspect(formGroup: FormGroup): Promise<void> {
        const postInitializeProspect = this._operationService.postProspectDataInitializeProspect(
            formGroup.value
        );
        try {
            formGroup.setErrors({});
            await postInitializeProspect.toPromise();
            this._modalService.closeModal();
            this._alertService.success(this.prospectCreationSuccessMessage);
            // TODO: Bring back when we use server side pagination.
            // this.loadProspects();
        } catch (err) {
            formGroup.setErrors({ err: err.message });
        }
    }

    public async prospectInvitation(formGroup: FormGroup): Promise<void> {
        const postProspectInvitation = this._operationService.postProspectDataInvitation(
            formGroup.value
        );
        try {
            formGroup.setErrors({});
            await postProspectInvitation.toPromise();
            this._modalService.closeModal();
            this._alertService.success(this.emailSentSuccessMessage);
            // TODO: Bring back when we use server side pagination.
            // this.loadProspects();
        } catch (err) {
            formGroup.setErrors({ err: err.message });
        }
    }

    public getProspectLink(prospectId: string): string {
        return getDynamicRouteLink(this.adminManageProspectsLinkUrl, {
            prospectId
        });
    }

    public handleFilterChange(): void {
        let status = this.filtersFormGroup.value.prospectStatusFilter;
        let type = this.filtersFormGroup.value.prospectTypesFilter;
        let invitedBy = this.filtersFormGroup.value.invitedByFilter;
        const businessName = this.filtersFormGroup.value.prospectName;
        if (this.filtersFormGroup.value.prospectStatusFilter === this.allProspectStatus) {
            status = undefined;
        }
        if (this.filtersFormGroup.value.prospectTypesFilter === this.allProspectTypes) {
            type = undefined;
        }
        if (this.filtersFormGroup.value.invitedByFilter === this.allInvitedBy) {
            invitedBy = undefined;
        }
        // TODO: Bring back when we use server side pagination.
        // this.loadProspects(undefined, businessName, status);
        this._adminProspectsService.setFilters(businessName, status, type, invitedBy);
        const hardFilter = status !== this.allProspectTypes;
        this._adminProspectsService.filterProspects(undefined, undefined, hardFilter);
        this.setTableSkipTake(0, this.tableDataSource.take);
    }

    public async handleProspectListRequestChange(requestUpdate: any): Promise<void> {
        this._sort$.next({
            sortColumn: requestUpdate.sortColumn,
            sortDirection: requestUpdate.sortDirection
        });
    }

    public loadRows(args): Observable<any> {
        this._loading.next(true);
        return this._prospectService.getProspectDataLight(...args).pipe(
            catchError(() => of([])),
            finalize(() => {
                this._loading.next(false);
            })
        );
    }

    public sortProspects(prospects: ProspectData[]): ProspectData[] {
        if (this.tableDataSource.sortColumn && this.tableDataSource.sortDirection) {
            prospects = orderBy(
                prospects,
                [
                    (prospect: ProspectData) =>
                        prospect[this.tableDataSource.sortColumn]?.toLowerCase()
                ],
                [OrderSortOption[this.tableDataSource.sortDirection]]
            ) as ProspectData[];
        }
        return prospects;
    }

    public parseDate(date: Date): string {
        return moment(date).format(DateFormat);
    }

    public setTableSkipTake(skip: number, take: number) {
        this.tableDataSource.skip = skip;
        this.tableDataSource.take = take;
    }
}

export const OrderSortOption = {
    '1': 'asc',
    '-1': 'desc'
};

export const DateFormat = 'MM/DD/YY [at] h:mm A [ET]';
